// Typed client for the Ayonix Studio API (Docs/API.md). Same-origin cookie auth.

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public data: Record<string, unknown> = {}) {
    super(message);
  }
  get upgrade() { return this.status === 402 || this.data.upgrade === true; }
}

type Body = Record<string, unknown> | unknown[] | Blob | ArrayBuffer | null | undefined;

export interface RequestOpts {
  method?: string;
  body?: Body;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  query?: Record<string, string | number | boolean | null | undefined>;
}

export function buildPath(path: string, query?: RequestOpts['query']) {
  if (!query) return path;
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null && v !== '' && v !== false) p.set(k, String(v));
  const s = p.toString();
  return s ? `${path}?${s}` : path;
}

export async function api<T = any>(path: string, opts: RequestOpts = {}): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json', ...opts.headers };
  let body: BodyInit | undefined;
  if (opts.body instanceof Blob || opts.body instanceof ArrayBuffer) body = opts.body;
  else if (opts.body !== undefined && opts.body !== null) { body = JSON.stringify(opts.body); headers['content-type'] = 'application/json'; }
  let res: Response;
  try {
    res = await fetch(buildPath(path, opts.query), { method: opts.method ?? (body ? 'POST' : 'GET'), headers, body, credentials: 'same-origin', signal: opts.signal });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError(0, navigator.onLine ? 'network' : 'offline', navigator.onLine ? 'Can’t reach Ayonix Studio right now. Check your connection and try again.' : 'You’re offline. Reconnect and try again.');
  }
  const text = await res.text();
  let data: any = null;
  if (text) { try { data = JSON.parse(text); } catch { data = null; } }
  if (!res.ok) {
    const err = data?.error;
    const e = new ApiError(res.status, err?.code ?? 'http_' + res.status, err?.message ?? `Request failed (HTTP ${res.status}).`, err ?? {});
    if (res.status === 401 && e.code === 'unauthenticated') window.dispatchEvent(new CustomEvent('ayx:unauthenticated'));
    throw e;
  }
  return data as T;
}

export const errorMessage = (e: unknown) => e instanceof ApiError || e instanceof Error ? e.message : 'Something went wrong.';

// ---------- Types ----------
export interface User { id: string; email: string; role: 'user' | 'admin'; emailVerified?: boolean; hostedSuspended?: boolean }
export interface Limits {
  hosted_jobs_per_day: number; hosted_max_bytes: number; hosted_retention_hours: number; hosted_concurrent_jobs: number;
  transcription_minutes_per_month: number; summaries_per_month: number; ai_discovery_per_day: number; library_items: number;
  discovery_requests_per_day?: number; translations_per_month?: number;
}
/** Plan ids: 'free' | 'pro' (shown as "Creator") | 'studio'. Unlock features by limits, never by comparing ids. */
export interface Entitlement {
  plan: string; planName?: string; source: 'stripe' | 'app_store' | null; status: string | null; currentPeriodEnd: number | null;
  cancelAtPeriodEnd: boolean; inGracePeriod: boolean; limits: Limits;
}
export interface Usage {
  hosted_jobs_today: number; transcription_seconds_this_month: number; summaries_this_month: number; ai_discovery_today: number;
  discovery_requests_today?: number; translations_this_month?: number;
}
export interface Me { user: User; entitlement: Entitlement; usage: Usage; hasBillingAccount: boolean; catalogVersion: string }

export interface Video {
  id: string; title: string; channelId: string; channelTitle: string; description: string; publishedAt: string;
  thumbnail: string | null; durationSec: number | null; definition: 'hd' | 'sd' | null; hasCaptions: boolean;
  viewCount: number | null; likeCount: number | null; tags: string[]; categoryId: string; embeddable: boolean; language: string | null;
}
export interface SearchResult { videos: Video[]; nextPageToken: string | null; prevPageToken: string | null; totalResults: number; unavailable: number }

export type JobStatus = 'queued' | 'fetching' | 'completed' | 'failed' | 'cancelled' | 'expired';
export interface Job {
  id: string; url: string; host: string; status: JobStatus; bytes_total: number | null; bytes_done: number; content_type: string | null;
  media_kind: 'video' | 'audio' | null; filename: string | null; error: string | null; attempts: number;
  created_at: number; updated_at: number; completed_at: number | null; expires_at: number | null;
}

export type LibraryKind = 'link' | 'youtube' | 'device_file' | 'hosted' | 'transcript';
export interface LibraryItem {
  id: string; kind: LibraryKind; url: string; title: string; collection: string | null; favorite: boolean;
  watchLater?: boolean; deletedAt?: number | null; meta: Record<string, any> | null; created_at: number;
}
export type LibraryView = 'all' | 'favorites' | 'watch_later' | 'trash' | 'videos' | 'links' | 'hosted' | 'transcripts';
export type LibraryCounts = Partial<Record<LibraryView, number>>;

export interface Format {
  label: string; width: number | null; height: number | null; container: string; mime: string; codecs: string | null;
  bytes: number | null; bitrate: number | null; url: string; original: boolean; hostedAllowed: boolean;
}
export interface Inspection {
  provider: 'wikimedia' | 'youtube' | 'direct'; title: string; thumbnail: string | null; durationSec: number | null;
  source: { name: string; pageUrl: string; license: string | null; author: string | null };
  download: { allowed: boolean; reason?: string };
  video: Format[]; audio: Format[]; subtitles: { lang: string; label: string; url: string }[];
  /** Set by the in-browser probe for direct links. */
  resumable?: boolean | null;
}

export interface ReleaseAsset { name: string; url: string; bytes: number; sha256?: string; key?: string }
export interface Release { tag: string; name: string; publishedAt: string; prerelease: boolean; notes: string; assets: { mac?: ReleaseAsset; windows?: ReleaseAsset } }

export interface Transcript {
  id: string; title: string; source_ref?: string | null; status: 'receiving' | 'completed' | 'empty' | 'cancelled' | 'failed';
  duration_sec: number; detected_language: string | null; chunk_count?: number; error: string | null; created_at: number; updated_at?: number; model?: string; language?: string | null;
}
export interface Segment { n: number; start: number; end: number; text: string }
export interface Citation { segment: number; start: number; end: number; excerpt: string }
export interface Summary {
  id: string; model: string; createdAt: number;
  overview: { text: string; citations: Citation[] } | null;
  takeaways: { text: string; citations: Citation[] }[];
  chapters: { title: string; start: number; segment: number }[];
  topics: { label: string; citations: Citation[] }[];
  dropped: unknown[];
}
export interface Translation { lang: string; segments: Segment[] }
